import React, { useState, useMemo, useCallback } from 'react';
import TextInput from '../TextInput/TextInput';
import type { Props as TextInputProps } from '../TextInput/TextInput';
import BaseInputIcon from '../BaseInput/BaseInputIcon';

export type TelephoneInputProps = TextInputProps & {
  initialValue: string;
};

export const formatPhone = (val: string): string => {
  const numericValue = val.replace(/\D/g, '').substring(0, 10);

  if (numericValue.length === 0) return '';

  if (numericValue.length <= 3) {
    return `(${numericValue}`;
  }

  if (numericValue.length <= 6) {
    return `(${numericValue.slice(0, 3)}) ${numericValue.slice(3)}`;
  }

  return `(${numericValue.slice(0, 3)}) ${numericValue.slice(
    3,
    6
  )}-${numericValue.slice(6, 10)}`;
};

const TelephoneInput: React.FC<TelephoneInputProps> = (props) => {
  const {
    placeholder = '(555)123-4567',
    label = 'Phone number',
    onChange,
    initialValue,
    ...commonProps
  } = props;

  const [value, setValue] = useState(initialValue); // stripped out dashes, "raw" value
  const valueToDisplay = useMemo(() => formatPhone(value), [value]);

  const validInputIcon = useMemo(
    () =>
      value.length === 10 ? (
        <BaseInputIcon
          aria-label="Valid"
          icon="checkmarkCircleInverted"
          color={{
            type: 'theme',
            colorLegacy: 'Green/50',
            colorNext: 'success/default',
          }}
        />
      ) : null,
    [value]
  );

  const handleChange = useCallback(
    (v: string) => {
      const newValue = v.replace(/\D/g, '').substring(0, 10);
      setValue(newValue);
      if (onChange) {
        onChange(newValue);
      }
    },
    [onChange, setValue]
  );

  return (
    <TextInput
      {...commonProps}
      label={label}
      onChange={handleChange}
      value={valueToDisplay}
      placeholder={placeholder}
      adornmentRight={validInputIcon}
      autoComplete="tel"
    />
  );
};

export default TelephoneInput;
